import { pauseAllMedia } from '@/scripts/core/global'
import { TsDOM as q, debounce } from '@/scripts/core/TsDOM'
import {
	isSliderComponent,
	type SliderComponent,
} from '@/scripts/theme/slider-component'
import { type StickyHeader } from '@/scripts/theme/sticky-header'
import { type DeferredMedia } from '@/scripts/theme/deferred-media'
import { type SlideChangedEvent } from '@/scripts/types/events'
import { UcoastEl } from '@/scripts/core/UcoastEl'

export class MediaGallery extends UcoastEl {
	static htmlSelector = 'media-gallery'
	elements: {
		liveRegion?: HTMLElement
		viewer: HTMLElement | SliderComponent
		thumbnails?: SliderComponent
	}
	mql: MediaQueryList
	stickyHeader?: StickyHeader
	constructor() {
		super()
		this.elements = {
			liveRegion: q.os('[id^="GalleryStatus"]', this),
			viewer: q.rs('[id^="GalleryViewer"]', this),
			thumbnails: q.os('[id^="GalleryThumbnails"]', this),
		}
		this.mql = window.matchMedia('(min-width: 750px)')
		if (!this.elements.thumbnails) return

		this.elements.viewer.addEventListener(
			'slideChanged',
			debounce(this.onSlideChanged.bind(this), 500)
		)
		this.elements.thumbnails
			.querySelectorAll('[data-target]')
			.forEach((mediaToSwitch) => {
				if (!(mediaToSwitch instanceof HTMLElement)) return
				const button = q.rs('button', mediaToSwitch)
				button.addEventListener(
					'click',
					this.setActiveMedia.bind(
						this,
						mediaToSwitch.dataset.target,
						false
					)
				)
			})
		if (
			this.dataset.desktopLayout?.includes('thumbnail') &&
			this.mql.matches
		)
			this.removeListSemantic()
	}

	getActiveMediaParent(mediaId: string) {
		return q.rs(
			`[data-media-id="${mediaId}"]`,
			this.elements.viewer,
			'parentElement'
		)
	}

	getActiveThumbnail(mediaId: string) {
		if (!this.elements.thumbnails) throw new Error('thumbnails is null')
		return q.rs(`[data-target="${mediaId}"]`, this.elements.thumbnails)
	}

	onSlideChanged(event: SlideChangedEvent) {
		if (!this.elements.thumbnails) return
		const mediaId = q.ra(event.detail.currentElement, 'data-media-id')
		const thumbnail = this.getActiveThumbnail(mediaId)
		this.setActiveThumbnail(thumbnail)
	}

	setActiveMedia(mediaId: string, prepend: boolean) {
		// Search in viewer first; if not found, search entire gallery
		// (handles featured image rendered outside the swiper on desktop)
		let activeMedia = q.os<HTMLElement>(
			`[data-media-id="${mediaId}"]`,
			this.elements.viewer
		)
		const isInViewer = !!activeMedia

		if (!activeMedia) {
			activeMedia = q.os<HTMLElement>(
				`[data-media-id="${mediaId}"]`,
				this
			)
		}
		if (!activeMedia) return

		this.elements.viewer
			.querySelectorAll('[data-media-id]')
			.forEach((element) => {
				element.classList.remove('is-active')
			})
		// Also clear is-active from the featured image if it exists
		const featuredImage = this.querySelector<HTMLElement>(
			'.featured__image'
		)
		featuredImage?.classList.remove('is-active')

		activeMedia.classList.add('is-active')

		if (prepend) {
			if (isInViewer) {
				// Target is a swiper slide — swap with the featured image on desktop
				if (featuredImage && !featuredImage.classList.contains('swiper-slide')) {
					this.swapFeaturedWithSlide(featuredImage, activeMedia, mediaId)
				} else {
					// Mobile (featured is already in swiper) — just prepend
					const swiperWrapper = this.elements.viewer.querySelector('.swiper-wrapper')
					if (swiperWrapper) swiperWrapper.prepend(activeMedia)
				}
				if (this.elements.thumbnails) {
					const activeThumbnail = q.os<HTMLElement>(
						`[data-target="${mediaId}"]`,
						this.elements.thumbnails
					)
					if (activeThumbnail) {
						const thumbsWrapper =
							this.elements.thumbnails.querySelector('.swiper-wrapper')
						if (thumbsWrapper) thumbsWrapper.prepend(activeThumbnail)
					}
				}
				if (isSliderComponent(this.elements.viewer))
					this.elements.viewer.resetPages()
			}
			// If target is the featured image (not in viewer), it's already
			// displayed prominently on desktop — no DOM move needed.
		}

		this.preventStickyHeader()
		window.setTimeout(() => {
			if (this.elements.thumbnails && isInViewer) {
				const parent = activeMedia!.parentElement
				if (parent) parent.scrollTo({ left: activeMedia!.offsetLeft })
			}
			if (
				!this.elements.thumbnails ||
				this.dataset.desktopLayout === 'stacked'
			) {
				activeMedia!.scrollIntoView({ behavior: 'smooth' })
			}
		})
		this.playActiveMedia(activeMedia)

		if (!this.elements.thumbnails) return
		const activeThumbnail = q.os<HTMLElement>(
			`[data-target="${mediaId}"]`,
			this.elements.thumbnails
		)
		if (!activeThumbnail) return
		this.setActiveThumbnail(activeThumbnail)
		this.announceLiveRegion(
			activeMedia,
			q.ra(activeThumbnail, 'data-media-position')
		)
	}

	/**
	 * On desktop, swap the featured image content with a swiper slide's content.
	 * This keeps the DOM structure intact (Swiper slide elements stay in place,
	 * MainGallery's featured image reference stays valid) while visually showing
	 * the selected variant's media in the featured (hero) position.
	 */
	private swapFeaturedWithSlide(
		featured: HTMLElement,
		slide: HTMLElement,
		newMediaId: string
	) {
		const oldFeaturedId = featured.getAttribute('data-media-id') ?? ''

		// Swap innerHTML between featured image and target slide
		const featuredHTML = featured.innerHTML
		featured.innerHTML = slide.innerHTML
		slide.innerHTML = featuredHTML

		// Swap data-media-id so future lookups still work
		featured.setAttribute('data-media-id', newMediaId)
		slide.setAttribute('data-media-id', oldFeaturedId)

		// Swap the id attributes so Slide-{sectionId}-{mediaId} stays correct
		const featuredDomId = featured.getAttribute('id') ?? ''
		const slideDomId = slide.getAttribute('id') ?? ''
		if (featuredDomId) slide.setAttribute('id', featuredDomId)
		if (slideDomId) featured.setAttribute('id', slideDomId)
	}

	setActiveThumbnail(thumbnail: HTMLElement) {
		if (!this.elements.thumbnails || !thumbnail) return

		this.elements.thumbnails
			.querySelectorAll('button')
			.forEach((element) => element.removeAttribute('aria-current'))
		const button = q.rs('button', thumbnail)
		button.setAttribute('aria-current', 'true')
		if (
			this.elements.thumbnails &&
			this.elements.thumbnails.isSlideVisible(thumbnail, 10)
		)
			return

		this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft })
	}

	announceLiveRegion(activeItem: HTMLElement, position: string) {
		if (!this.elements.liveRegion) return
		const image = q.rs<HTMLImageElement>(
			'.product__modal-opener--image img',
			activeItem
		)
		if (!image) return
		const liveRegion = this.elements.liveRegion
		image.onload = () => {
			liveRegion.setAttribute('aria-hidden', 'false')
			liveRegion.innerHTML =
				window.accessibilityStrings.imageAvailable.replace(
					'[index]',
					position
				)
			setTimeout(() => {
				liveRegion.setAttribute('aria-hidden', 'true')
			}, 2000)
		}
		image.src = image.src
	}

	playActiveMedia(activeItem: HTMLElement) {
		pauseAllMedia()
		const deferredMedia = q.os<DeferredMedia>('.deferred-media', activeItem)
		if (deferredMedia) deferredMedia.loadContent(false)
	}

	preventStickyHeader() {
		this.stickyHeader =
			this.stickyHeader || q.rs<StickyHeader>('sticky-header')
		if (!this.stickyHeader) return
		this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'))
	}

	removeListSemantic() {
		if (!isSliderComponent(this.elements.viewer)) return
		this.elements.viewer.slider.setAttribute('role', 'presentation')
		this.elements.viewer.sliderItems?.forEach((slide) =>
			slide.setAttribute('role', 'presentation')
		)
	}
}
